// Service layer for rubric CRUD operations and database interactions
// This file handles all Firestore database operations related to rubrics

import { db } from '../firebase';
import {
  collection, // Reference to a collection in Firestore
  getDocs, // Get all documents from a collection
  addDoc, // Add a new document to a collection
  doc, // Reference to a specific document
  updateDoc, // Update an existing document
  deleteDoc, // Delete a document
  query, // Create a query for filtering documents
  where, // Add a where clause to a query
  getDoc, // Get a single document
  Timestamp,
} from 'firebase/firestore';
import {
  Rubric,
  RubricQuestion,
  ProjectRubricScore,
} from '../types/rubric.types';

/**
 * Creates a new rubric in the Firestore database
 * New rubrics are automatically active and count towards the 3-rubric limit
 * @param name - Display name for the rubric
 * @param fairId - ID of the fair this rubric belongs to
 * @param createdBy - User ID of the admin creating the rubric
 * @param questions - Array of questions (without IDs, they'll be generated)
 * @returns Promise<string> - The ID of the newly created rubric
 * @throws Error if fair already has 3 active rubrics
 */
export const createRubric = async (
  name: string,
  fairId: string,
  createdBy: string,
  questions: Omit<RubricQuestion, 'id'>[] // Omit removes 'id' from RubricQuestion type
): Promise<string> => {
  // Check if fair already has 3 rubrics total (active or inactive)
  const rubricsRef = collection(db, 'rubrics');
  const fairQuery = query(rubricsRef, where('fairId', '==', fairId));
  const fairSnapshot = await getDocs(fairQuery);

  if (fairSnapshot.size >= 3) {
    throw new Error(
      'Cannot create rubric: Fair already has 3 rubrics (maximum limit). Please delete one first.'
    );
  }

  // Calculate total points by summing all maxPoints from questions
  const totalPoints = questions.reduce((sum, q) => sum + q.maxPoints, 0);

  // Prepare the data object for Firestore
  const rubricData = {
    name,
    fairId,
    createdBy,
    questions,
    totalPoints,
    isActive: true, // New rubrics are immediately active
    createdAt: Date.now(), // Use timestamp number instead of Date object
    updatedAt: Date.now(),
  };

  try {
    // Add document to 'rubrics' collection and get the reference
    const docRef = await addDoc(collection(db, 'rubrics'), rubricData);
    return docRef.id; // Return the auto-generated document ID
  } catch (error) {
    console.error('Error creating rubric:', error);
    throw error; // Re-throw so calling code can handle the error
  }
};
/**
 * Retrieves all rubrics from the database
 * @returns Promise<Rubric[]> - Array of all rubrics
 */
export const getRubrics = async (): Promise<Rubric[]> => {
  try {
    // Get reference to the 'rubrics' collection
    const rubricsRef = collection(db, 'rubrics');
    // Fetch all documents in the collection
    const querySnapshot = await getDocs(rubricsRef);

    // Transform Firestore documents to our Rubric type
    return querySnapshot.docs.map((doc) => ({
      id: doc.id, // Firestore document ID
      ...doc.data(), // Spread all other document data
    })) as Rubric[];
  } catch (error) {
    console.error('Error fetching rubrics:', error);
    throw error;
  }
};

/**
 * Gets the currently active rubric (the one judges should use for scoring)
 * @deprecated Use getActiveRubricsByFair instead for multi-fair support
 * @returns Promise<Rubric | null> - The active rubric or null if none exists
 */
export const getActiveRubric = async (): Promise<Rubric | null> => {
  try {
    const rubricsRef = collection(db, 'rubrics');
    // Create a query to find documents where isActive === true
    const q = query(rubricsRef, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);

    // If no active rubric found, return null
    if (querySnapshot.empty) return null;

    // Return the first (and should be only) active rubric
    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data(),
    } as Rubric;
  } catch (error) {
    console.error('Error fetching active rubric:', error);
    throw error;
  }
};

/**
 * Gets all rubrics for a specific fair
 * @param fairId - ID of the fair
 * @returns Promise<Rubric[]> - Array of all rubrics for this fair
 */
export const getRubricsByFair = async (fairId: string): Promise<Rubric[]> => {
  try {
    const rubricsRef = collection(db, 'rubrics');
    const q = query(rubricsRef, where('fairId', '==', fairId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Rubric[];
  } catch (error) {
    console.error('Error fetching rubrics by fair:', error);
    throw error;
  }
};

/**
 * Gets all active rubrics for a specific fair
 * @param fairId - ID of the fair
 * @returns Promise<Rubric[]> - Array of active rubrics (max 3)
 */
export const getActiveRubricsByFair = async (
  fairId: string
): Promise<Rubric[]> => {
  try {
    const rubricsRef = collection(db, 'rubrics');
    const q = query(
      rubricsRef,
      where('fairId', '==', fairId),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Rubric[];
  } catch (error) {
    console.error('Error fetching active rubrics by fair:', error);
    throw error;
  }
};

/**
 * Sets a specific rubric as active for its fair
 * @param rubricId - ID of the rubric to make active
 */
export const setRubricAsActive = async (rubricId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'rubrics', rubricId), {
      isActive: true,
      updatedAt: Date.now(),
    });
  } catch (error) {
    console.error('Error setting rubric as active:', error);
    throw error;
  }
};

/**
 * Deactivates a rubric
 * @param rubricId - ID of the rubric to deactivate
 */
export const setRubricAsInactive = async (rubricId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'rubrics', rubricId), {
      isActive: false,
      updatedAt: Date.now(),
    });
  } catch (error) {
    console.error('Error deactivating rubric:', error);
    throw error;
  }
};

/**
 * Gets a specific rubric by its ID
 * @param rubricId - The ID of the rubric to retrieve
 * @returns Promise<Rubric | null> - The rubric or null if not found
 */
export const getRubricById = async (
  rubricId: string
): Promise<Rubric | null> => {
  // Get reference to specific document
  const snap = await getDoc(doc(db, 'rubrics', rubricId));

  // Check if document exists, then return formatted data or null
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Rubric) : null;
};

/**
 * Updates an existing rubric with new data
 * @param rubricId - ID of the rubric to update
 * @param updates - Partial data containing only the fields to update
 */
export const updateRubric = async (
  rubricId: string,
  updates: Partial<Pick<Rubric, 'name' | 'questions'>> // Only allow updating name and questions
): Promise<void> => {
  // If questions are being updated, recalculate total points
  const totalPoints = updates.questions
    ? updates.questions.reduce((sum, q) => sum + q.maxPoints, 0)
    : undefined;

  // Build update payload with only provided fields
  const payload = {
    ...(updates.name != null ? { name: updates.name } : {}),
    ...(updates.questions != null ? { questions: updates.questions } : {}),
    ...(totalPoints != null ? { totalPoints } : {}),
    updatedAt: Date.now(), // Always update the timestamp
  };

  // Update the document in Firestore
  await updateDoc(doc(db, 'rubrics', rubricId), payload);
};

/**
 * Permanently deletes a rubric from the database
 * @param rubricId - ID of the rubric to delete
 */
export const deleteRubricById = async (rubricId: string): Promise<void> => {
  await deleteDoc(doc(db, 'rubrics', rubricId));
};

// ===== RUBRIC SCORING FUNCTIONS =====

/**
 * Saves a judge's rubric score for a project
 * @param projectId - ID of the project being scored
 * @param judgeId - ID of the judge submitting the score
 * @param rubricId - ID of the rubric used for scoring
 * @param scores - Array of scores for each question
 * @param totalScore - Sum of all points awarded
 */
export const saveRubricScore = async (
  projectId: string,
  judgeId: string,
  rubricId: string,
  scores: { questionId: string; pointsAwarded: number }[],
  totalScore: number
): Promise<string> => {
  try {
    // Get judge name from users collection
    const judgeDoc = await getDoc(doc(db, 'users', judgeId));
    const judgeData = judgeDoc.data();
    const judgeName = judgeData
      ? `${judgeData.firstName || ''} ${judgeData.lastName || ''}`.trim()
      : 'Unknown Judge';

    const timestamp = new Date();

    // Save to projectScores with old structure
    const scoreData = {
      projectId,
      judgeId,
      judgeName,
      score: totalScore, // Map totalScore to score field
      comments: [], // Empty comments array to match old structure
      createdAt: Timestamp.fromDate(timestamp),
      updatedAt: Timestamp.fromDate(timestamp),
      isAssigned: false,
      status: 'completed',
      // Store rubric metadata for reference
      rubricId,
      rubricScores: scores,
    };

    const docRef = await addDoc(collection(db, 'projectScores'), scoreData);
    console.log(`Rubric score saved to projectScores with ID: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Error saving rubric score:', error);
    throw error;
  }
};

/**
 * Gets all rubric scores for a specific project
 * @param projectId - ID of the project
 * @returns Promise<ProjectRubricScore[]> - Array of all scores for the project
 */
export const getProjectRubricScores = async (
  projectId: string
): Promise<ProjectRubricScore[]> => {
  try {
    const scoresRef = collection(db, 'rubricScores');
    const q = query(scoresRef, where('projectId', '==', projectId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as ProjectRubricScore[];
  } catch (error) {
    console.error('Error fetching project rubric scores:', error);
    throw error;
  }
};

/**
 * Gets a specific judge's rubric score for a project
 * @param projectId - ID of the project
 * @param judgeId - ID of the judge
 * @returns Promise<ProjectRubricScore | null> - The score or null if not found
 */
export const getJudgeRubricScore = async (
  projectId: string,
  judgeId: string
): Promise<ProjectRubricScore | null> => {
  try {
    const scoresRef = collection(db, 'rubricScores');
    const q = query(
      scoresRef,
      where('projectId', '==', projectId),
      where('judgeId', '==', judgeId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return null;

    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data(),
    } as ProjectRubricScore;
  } catch (error) {
    console.error('Error fetching judge rubric score:', error);
    throw error;
  }
};

/**
 * Calculates average rubric score for a project across all judges
 * @param projectId - ID of the project
 * @returns Promise<{ averageScore: number; totalJudges: number; scores: ProjectRubricScore[] }>
 */
export const getProjectRubricAverage = async (
  projectId: string
): Promise<{
  averageScore: number;
  totalJudges: number;
  scores: ProjectRubricScore[];
}> => {
  try {
    const scores = await getProjectRubricScores(projectId);

    if (scores.length === 0) {
      return { averageScore: 0, totalJudges: 0, scores: [] };
    }

    const totalScore = scores.reduce((sum, score) => sum + score.totalScore, 0);
    const averageScore = totalScore / scores.length;

    return {
      averageScore: Math.round(averageScore * 100) / 100, // Round to 2 decimal places
      totalJudges: scores.length,
      scores,
    };
  } catch (error) {
    console.error('Error calculating project rubric average:', error);
    throw error;
  }
};
